import React from "react";
import {
  CompleteMaintenanceTaskDto,
  MaintenanceTaskDto,
  maintenanceTasksApi,
} from "../../../services/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, todayDateInputValue } from "../utils";

interface Props {
  task: MaintenanceTaskDto;
  onClose: () => void;
  onSaved: () => void;
}

export const CompleteTaskDialog: React.FC<Props> = ({ task, onClose, onSaved }) => {
  const [completedAt, setCompletedAt] = React.useState(todayDateInputValue());
  const [notes, setNotes] = React.useState("");
  const [costAmount, setCostAmount] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    if (!completedAt) { window.alert("Indica la fecha en que se hizo"); return; }

    // Si ya hay un completado posterior, pedimos confirmación: no sobrescribirá la próxima
    // revisión (esa se sigue calculando desde el más reciente), pero queremos que el usuario
    // sea consciente de lo que está haciendo.
    if (task.lastCompletedAt) {
      const newDate = new Date(completedAt + "T12:00:00").getTime();
      const lastDate = new Date(task.lastCompletedAt).getTime();
      if (newDate < lastDate) {
        const confirmed = window.confirm(
          `Ya hay una revisión registrada el ${formatDate(task.lastCompletedAt, false)}, posterior a la que vas a registrar (${formatDate(completedAt, false)}).\n\n` +
          `Se guardará en el historial pero la próxima revisión seguirá calculándose desde la fecha más reciente. ¿Continuar?`
        );
        if (!confirmed) return;
      }
    }

    setSaving(true);
    try {
      const payload: CompleteMaintenanceTaskDto = {
        completedAt: new Date(completedAt + "T12:00:00").toISOString(),
        notes: notes.trim() || null,
        costAmount: costAmount ? parseFloat(costAmount) : null,
        attachmentUrl: null,
      };
      await maintenanceTasksApi.complete(task.id, payload);
      onSaved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar tarea completada</DialogTitle>
        </DialogHeader>

        <div className="text-sm text-slate-600 mb-2">
          <span className="font-medium">{task.equipmentName}</span> — {task.title}
        </div>

        <div className="space-y-3">
          <div>
            <Label>Fecha de realización *</Label>
            <Input type="date" value={completedAt} onChange={(e) => setCompletedAt(e.target.value)} />
            <p className="text-xs text-slate-500 mt-1">
              <strong>¿Cuándo se hizo?</strong> No cuándo lo registras. Si fue ayer, pon ayer:
              la próxima fecha de aviso se calcula desde aquí.
            </p>
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Qué se hizo, repuestos usados…" />
          </div>
          <div>
            <Label>Coste (€)</Label>
            <Input type="number" min={0} step="0.01" value={costAmount} onChange={(e) => setCostAmount(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Guardando…" : "Registrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
